import React, {useEffect, useState} from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    StyledEngineProvider,
    TextField,
    ThemeProvider
} from '@mui/material'

import RadioButton from "./RadioButton";

import {FormattedMessage, injectIntl} from 'react-intl'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ship from 'api/order/ship';

import {LineItemStatus} from "utils/LineItemStatus";
import {NoTracking, ShippingMethods} from "utils/ShippingMethods";
import {connect} from "react-redux";
import { waybillTheme, waybillStyle } from "assets/jss/components/modal/waybillSelector";
import {withStyles} from "@mui/styles";

function ModalWaybillSelector(props) {
    const {
        classes,
        lineItems,
        lineItemSelectedIds,
        selectedShipment,
        open,
        onClose
    } = props

    const [selectedSelector, setSelectedSelector] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [selfShippingNumber, setSelfShippingNumber] = useState('')
    const [shipmentLoading, setShipmentLoading] = useState(false);
    const [shippingWeight, setShippingWeight] = useState(0)

    const [selectedLineItems, setSelectedLineItems] = useState([])
    const [isClickAndCollectType, setIsClickAndCollectType] = useState(false)
    const [isLocker, setIsLocker] = useState(false)
    const [shouldPrint, setShouldPrint] = useState(false)
    const [isShipWithAPI, setIsShipWithAPI] = useState(false)

    const shippingSelector = 'shipping'
    const selfShippingSelector = 'self-shipping'
    const customerSelector = 'customer'

    useEffect(() => {
        const selectedItems = getSelectedLineItems();
        if (selectedItems.length === 0) {
            return;
        }

        setSelectedLineItems(selectedItems);

        // Only one shippingMethod possible per shipment
        const shippingMethod = selectedItems[0].shippingMethod;

        const weight = getWeight(selectedItems);
        setShippingWeight(weight.toFixed(2));

        setIsLocker(shippingMethod === ShippingMethods.LOCKER);
        const isClickAndCollect = shippingMethod.type === ShippingMethods.CLICK_COLLECT
            || shippingMethod.type === ShippingMethods.DRIVE
            || shippingMethod.type === ShippingMethods.LOCKER;

        setIsClickAndCollectType(isClickAndCollect);
        setShouldPrint(shippingMethod.printReturnLabel || shippingMethod.printReturnForm || shippingMethod.clickAndCollectTwoStepsForced);

        setIsShipWithAPI(getIsShipWithAPI(selectedItems));

    }, [lineItemSelectedIds, selectedShipment])

    function getWeight(lineItems) {
        return lineItems
            .map(li => li.status === LineItemStatus.TO_SHIP ? (li?.variation?.weight ?? 0.3) : 0)
            .reduce((acc, value) => acc + value, 0)
    }

    function getIsShipWithAPI(lineItems) {
        return lineItems
            .map(li => li.shippingMethod.wsdl)
            .filter(wsdl => typeof wsdl === 'string')
            .length >= 1
            ;
    }

    const dialogTitleMessageId = () => {
        if (isClickAndCollectType) {
            if (shouldPrint) {
                if (isLocker) {
                    return 'picking.order.show.waybillSelector.title_locker_prepare'
                }

                return 'picking.order.show.waybillSelector.title_ClickAndCollect_prepare'
            }

            if (isLocker) {
                return 'picking.order.show.waybillSelector.title_locker'
            }

            return 'picking.order.show.waybillSelector.title_ClickAndCollect'
        }

        return 'picking.order.show.waybillSelector.title'
    }

    const clickAndCollectSelectorContentText = () => {
        let title = 'picking.order.show.waybillSelector.customer.title';
        let subtitle = 'picking.order.show.waybillSelector.customer.subtitle';

        if (shouldPrint) {
            title = 'picking.order.show.waybillSelector.generate.title';
            subtitle = 'picking.order.show.waybillSelector.generate.subtitle';
        }

        if (isLocker) {
            title = 'picking.order.show.waybillSelector.customer.locker.title';
            subtitle = 'picking.order.show.waybillSelector.customer.locker.subtitle'
        }

        return (
            <>
                <h3><FormattedMessage id={title} /></h3>
                <p><FormattedMessage id={subtitle} /></p>
            </>
        )
    }

    const submitButtonTitleMessageId = () => {
        if (isClickAndCollectType) {
            if (shouldPrint) {
                return 'picking.order.show.waybillSelector.action.confirm.generate'
            }

            if (isLocker) {
                return 'picking.order.show.waybillSelector.action.confirm.locker'
            }

            return 'picking.order.show.waybillSelector.action.confirm.clickcollect'
        }

        return 'picking.order.show.waybillSelector.action.confirm'
    }

    /**
     * Return the selected line items
     */
    function getSelectedLineItems() {
        if (selectedShipment) {
            return getLineItemsForShipment(selectedShipment);
        }

        return lineItems.filter(li => lineItemSelectedIds.includes(li['@id']))
    }

    /**
     * Return all line items for the given shipment
     */
    function getLineItemsForShipment(shipment) {
        return  lineItems.filter((item) => {
            return item.shipments.some((s) => {
                return s['@id'] === shipment['@id'];
            });
        });
    }

    function onSubmit() {
        setShipmentLoading(true);
        const selectedLineItemIds = selectedLineItems.map(lineItem => lineItem.id);

        const values = {
            'preferredPrinters': JSON.parse(localStorage.getItem(`${props.organization.id}_preferredPrinters`)),
            'lineItems': selectedLineItemIds,
        }

        if (selectedSelector === shippingSelector) {
            values.weight = shippingWeight;
            values.shouldPrint = true;
        } else {
            values.trackingNumber = selectedSelector === selfShippingSelector ? selfShippingNumber : NoTracking;
            values.shouldPrint = false;
        }

        if (selectedShipment) {
            values.shipmentId = selectedShipment['id']
        }

        ship(lineItems[0].order, values).then(() => {
            onClose();
            setShipmentLoading(false);
            props.refreshOrder(2000);
        }).catch(() => {
                setShipmentLoading(false);
                setErrorMessage('picking.order.show.waybillSelector.error');
            })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{ backdrop: { style: { backgroundColor: 'rgba(51, 51, 51, 0.20)' } } }}
            classes={{ paper: classes.paper }}
        >
            <DialogContent>
                <h2>
                    <FormattedMessage id={dialogTitleMessageId()} />
                </h2>

                {errorMessage !== '' && (
                    <div className={classes.errorMessage}><FormattedMessage id={errorMessage} /></div>
                )}

                {!isClickAndCollectType && (
                    <>
                        {isShipWithAPI && (
                            <div
                                className={classNames(
                                    classes.selector,
                                    classes.selectorShipping,
                                    {[classes.selected]: selectedSelector === shippingSelector}
                                )}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setSelectedSelector(shippingSelector)
                                }}
                            >
                                <RadioButton checked={selectedSelector === shippingSelector} />
                                <div>
                                    <h3><FormattedMessage id={'picking.order.show.waybillSelector.shipping.title'} /></h3>
                                    <p><FormattedMessage id={'picking.order.show.waybillSelector.shipping.subtitle'} /></p>
                                    {selectedSelector === shippingSelector && (
                                        <StyledEngineProvider injectFirst>
                                            <ThemeProvider theme={waybillTheme}>
                                                <TextField
                                                    variant='standard'
                                                    label={<FormattedMessage id={'picking.order.show.waybillSelector.shipping.weight.label'} />}
                                                    onChange={e => {
                                                        const weight = e.target.value.replace(',', '.');
                                                        setShippingWeight(weight);
                                                    }}
                                                    defaultValue={shippingWeight}
                                                    color='primary'
                                                    fullWidth
                                                />
                                            </ThemeProvider>
                                        </StyledEngineProvider>
                                    )}
                                </div>
                            </div>
                        )}
                        <div
                            className={classNames(
                                classes.selector,
                                classes.selectorSelfShipping,
                                {[classes.selected]: selectedSelector === selfShippingSelector}
                            )}
                            onClick={(e) => {
                                e.preventDefault()
                                setSelectedSelector(selfShippingSelector)
                            }}
                        >
                            <RadioButton checked={selectedSelector === selfShippingSelector} />
                            <div>
                                <h3><FormattedMessage id={'picking.order.show.waybillSelector.self_shipping.title'} /></h3>
                                <p><FormattedMessage id={'picking.order.show.waybillSelector.self_shipping.subtitle'} /></p>
                                {selectedSelector === selfShippingSelector && (
                                    <StyledEngineProvider injectFirst>
                                        <ThemeProvider theme={waybillTheme}>
                                            <TextField
                                                variant='standard'
                                                label={<FormattedMessage id={'picking.order.show.waybillSelector.self_shipping.number.label'} />}
                                                onChange={e => setSelfShippingNumber(e.target.value)}
                                                defaultValue={selfShippingNumber}
                                                color='primary'
                                                fullWidth
                                            />
                                        </ThemeProvider>
                                    </StyledEngineProvider>
                                )}
                            </div>
                        </div>
                    </>
                )}
                {isClickAndCollectType && (
                    <div
                        className={classNames(
                            classes.selector,
                            classes.selectorCustomer,
                            {[classes.selected]: selectedSelector === customerSelector}
                        )}
                        onClick={(e) => {
                            e.preventDefault()
                            setSelectedSelector(customerSelector)
                        }}
                    >
                        <RadioButton checked={selectedSelector === customerSelector} />
                        <div>
                            {clickAndCollectSelectorContentText()}
                        </div>
                    </div>
                )}
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    onClick={onClose}
                    className={classes.actionButton}
                >
                    <FormattedMessage id={'picking.order.show.waybillSelector.action.cancel'} />
                </Button>
                <Button
                    onClick={onSubmit}
                    className={classNames(classes.actionButton, classes.validatedButton)}
                    disabled={
                        shipmentLoading ||
                            selectedSelector === ''
                            || shippingWeight.indexOf(' ') >= 0
                            || selfShippingNumber.indexOf(' ') >= 0
                            || (selectedSelector === shippingSelector && shippingWeight === '')
                            || (selectedSelector === selfShippingSelector && selfShippingNumber === '')
                    }
                >
                    <FormattedMessage id={submitButtonTitleMessageId()} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ModalWaybillSelector.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    lineItems: PropTypes.array.isRequired,
    lineItemSelectedIds: PropTypes.array.isRequired,
}

export default connect(
    state => ({
        organization: state.currentOrganization.retrieved,
    })
)(withStyles(waybillStyle)(injectIntl(ModalWaybillSelector)));
